import { z } from "zod";
import {
  emailField,
  confirmEmailField,
  firstNameField,
  lastNameField,
  optionalEmailField,
} from "@core/schemas/fields";
import { withEmailConfirmation } from "@core/schemas/refinements";

/**
 * Base friend invitation schema with email confirmation validation
 */
export const baseFriendInvitationSchema = withEmailConfirmation(
  z.object({
    firstName: firstNameField,
    lastName: lastNameField,
    email: emailField,
    confirmEmail: confirmEmailField,
  })
);

export type FriendInvitationFormData = z.infer<
  typeof baseFriendInvitationSchema
>;

/**
 * Schema for a single invitee in the multi-invite form.
 * Uses optionalEmailField for field-level validation (shows error for invalid format).
 */
export const invitationItemSchema = z.object({
  email: optionalEmailField,
  firstName: z.string(),
  lastName: z.string(),
});

/**
 * Schema for the multi-invite invitations form.
 * Requires at least one valid email address.
 */
export const invitationsSchema = z.object({
  items: z
    .array(invitationItemSchema)
    .refine(
      (items) =>
        items.some(
          (item) =>
            item.email.trim() !== "" &&
            z.string().email().safeParse(item.email.trim()).success
        ),
      {
        message: "At least one valid email is required",
        path: ["items"],
      }
    ),
});

export type InvitationsFormData = z.infer<typeof invitationsSchema>;
export type InvitationItem = z.infer<typeof invitationItemSchema>;

// Backward compatibility aliases (deprecated)
/** @deprecated Use invitationItemSchema instead */
export const friendInvitationItemSchema = invitationItemSchema;
/** @deprecated Use invitationsSchema instead */
export const friendInvitationsSchema = invitationsSchema;
/** @deprecated Use InvitationsFormData instead */
export type FriendInvitationsFormData = InvitationsFormData;
/** @deprecated Use InvitationItem instead */
export type FriendInvitationItem = InvitationItem;
